/* eslint-disable import/no-cycle */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { userAccountInfo } from 'helpers/user'
import {
  getCardInfo,
  updateEnrolledEmail
} from 'redux/accountInfo/accountInfoSlice'
import { updateStatementState } from 'redux/statement/statementSlice'
import { AppThunkAction } from 'redux/store'
import eBillAPI from 'services/eBill'
import { EBillResponse } from 'services/eBill/typings'
import { EbillInfo } from 'typings/ebill'
import { ServerError } from 'typings/serverError'

interface EBillState {
  isLoading: boolean
  response: EBillResponse
  error: ServerError
}

export const initState: EBillState = {
  isLoading: false,
  response: null,
  error: null
}

const onEBillApiFailure =
  (errorResponse: ServerError): AppThunkAction =>
  dispatch => {
    const { response } = errorResponse as ServerError
    const { message } = response || {}

    dispatch(
      updateStatementState({
        error: true,
        errorMessage: message
      })
    )
  }

export const updateEbillProfile = createAsyncThunk(
  'eBillSlice/updateEbillProfile',
  async (payload: EbillInfo, { dispatch, rejectWithValue }) => {
    try {
      const response = await eBillAPI.getEbillProfile(payload)

      dispatch(updateStatementState({ isEbillProfileUpdated: true }))

      /*
       * After the eBill email has been updated there is no summary call to fetch the new email address
       * This is needed to update the global state so user can see the updated value in the session
       */
      dispatch(updateEnrolledEmail(payload.emailAddress))

      return response
    } catch (errorResponse) {
      dispatch(onEBillApiFailure(errorResponse))
      return rejectWithValue(errorResponse)
    }
  }
)

export const updateEbillEnrollment = createAsyncThunk(
  'eBillSlice/updateEbillEnrollment',
  async (payload: EbillInfo, { dispatch, rejectWithValue }) => {
    try {
      const { accountNumber, accessToken } = payload

      const response = await eBillAPI.updateEbillEnrollment(payload)

      const { isProx } = userAccountInfo

      dispatch(updateStatementState({ isEbillEnrollmentUpdated: true }))

      if (isProx) {
        dispatch(getCardInfo({ accountNumber, accessToken }))
      }

      return response
    } catch (errorResponse) {
      dispatch(onEBillApiFailure(errorResponse))
      return rejectWithValue(errorResponse)
    }
  }
)

export const eBillSlice = createSlice({
  name: 'eBillSlice',
  initialState: initState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(updateEbillProfile.pending, (state: EBillState) => {
        state.isLoading = true
      })
      .addCase(
        updateEbillProfile.fulfilled,
        (state: EBillState, action: PayloadAction<EBillResponse>) => {
          state.response = action.payload
          state.isLoading = false
        }
      )
      .addCase(
        updateEbillProfile.rejected,
        (state: EBillState, action: PayloadAction<unknown>) => {
          state.error = action.payload as ServerError
          state.isLoading = false
        }
      )
  }
})

export default eBillSlice.reducer
